/**
 * StartIntent — one typed, URL-serializable creation intent that every
 * entry point constructs and every editor consumes. See
 * docs/plans/2026-06-10-start-intent-and-seeding-plan.md.
 *
 * Wire format is flat for URL ergonomics:
 *   ?type=flowchart&seed=sample:swimlane-approval&wizard=skip&src=create-new:process
 *
 * Everything rides the query string (creation start-state is transient,
 * never path — per dashboard-routing.md's two-axis rule).
 */

export type StartSeed =
  | { kind: 'sample'; id: string }       // curated content from the Seed Registry
  | { kind: 'template'; id: string }     // clone an `isTemplate` diagram (server-side)
  | { kind: 'import'; hint?: string }    // open an import surface (csv | sop | scan | …)
  | { kind: 'agent'; prompt: string };   // AI-generate on first open

export type WizardPolicy = 'auto' | 'skip';

export interface StartIntent {
  /** Editor to open. Absent = type-selection / blank flow. */
  type?: string;
  /** What the canvas opens WITH. Absent = blank. */
  seed?: StartSeed;
  /** Assistant-panel start state ('import' | 'resources' | 'shapes' | …). */
  panel?: string;
  /** Wizard policy. 'auto' (default) = domain decides; 'skip' = force-skip. */
  wizard?: WizardPolicy;
  /** Optional default shape library hint for domains with grouped shape sets. */
  shapeSet?: string;
  /** Attribution — which door produced this creation (analytics). */
  src?: string;
}

const SEED_KINDS = ['sample', 'template', 'import', 'agent'] as const;
type SeedKind = (typeof SEED_KINDS)[number];

/** Parse a flat `seed=` value (`kind:rest`) into a StartSeed. The rest may
 *  itself contain colons (agent prompts) — only the first colon splits. */
export function parseSeed(raw: string | null | undefined): StartSeed | undefined {
  if (!raw) return undefined;
  const idx = raw.indexOf(':');
  const kind = (idx === -1 ? raw : raw.slice(0, idx)) as SeedKind;
  const rest = idx === -1 ? '' : raw.slice(idx + 1);
  if (!(SEED_KINDS as readonly string[]).includes(kind)) return undefined;
  switch (kind) {
    case 'sample':
    case 'template':
      return rest ? { kind, id: rest } : undefined;
    case 'import':
      return { kind, ...(rest ? { hint: rest } : {}) };
    case 'agent':
      return rest ? { kind, prompt: rest } : undefined;
  }
}

export function serializeSeed(seed: StartSeed): string {
  switch (seed.kind) {
    case 'sample':
    case 'template':
      return `${seed.kind}:${seed.id}`;
    case 'import':
      return seed.hint ? `import:${seed.hint}` : 'import';
    case 'agent':
      return `agent:${seed.prompt}`;
  }
}

/** Read a StartIntent from a search-params bag (object or URLSearchParams). */
export function parseStartIntent(
  search: Record<string, unknown> | URLSearchParams | null | undefined,
): StartIntent {
  const get = (k: string): string | undefined => {
    if (!search) return undefined;
    if (search instanceof URLSearchParams) return search.get(k) ?? undefined;
    const v = (search as Record<string, unknown>)[k];
    return typeof v === 'string' ? v : undefined;
  };

  const seed = parseSeed(get('seed'));
  const wizardRaw = get('wizard');
  const wizard: WizardPolicy | undefined =
    wizardRaw === 'skip' || wizardRaw === 'auto' ? wizardRaw : undefined;

  return {
    type: get('type'),
    seed,
    panel: get('panel'),
    wizard,
    shapeSet: get('shapeSet'),
    src: get('src'),
  };
}

/** Serialize a StartIntent to query params for appending to a `d/new` URL.
 *  Only sets keys that are present. */
export function serializeStartIntent(intent: StartIntent): Record<string, string> {
  const out: Record<string, string> = {};
  if (intent.type) out.type = intent.type;
  if (intent.seed) out.seed = serializeSeed(intent.seed);
  if (intent.panel) out.panel = intent.panel;
  if (intent.wizard) out.wizard = intent.wizard;
  if (intent.shapeSet) out.shapeSet = intent.shapeSet;
  if (intent.src) out.src = intent.src;
  return out;
}

/** Validate an already-STRUCTURED seed object (the round-tripped form, e.g.
 *  from sessionStorage) into a StartSeed. Counterpart to `parseSeed`, which
 *  parses the flat `kind:rest` wire string. */
export function coerceStartSeed(raw: unknown): StartSeed | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as Record<string, unknown>;
  if (s.kind === 'sample' && typeof s.id === 'string') return { kind: 'sample', id: s.id };
  if (s.kind === 'template' && typeof s.id === 'string') return { kind: 'template', id: s.id };
  if (s.kind === 'import') return { kind: 'import', ...(typeof s.hint === 'string' ? { hint: s.hint } : {}) };
  if (s.kind === 'agent' && typeof s.prompt === 'string') return { kind: 'agent', prompt: s.prompt };
  return undefined;
}

/** Validate an already-STRUCTURED StartIntent object (e.g. parsed from the
 *  onboarding sessionStorage blob) back into a typed StartIntent. Counterpart
 *  to `parseStartIntent`, which reads the flat URL search bag. Single source
 *  of truth for the shape so the two entry points can't drift. */
export function coerceStartIntent(raw: unknown): StartIntent | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const intent: StartIntent = {};
  if (typeof o.type === 'string') intent.type = o.type;
  if (typeof o.panel === 'string') intent.panel = o.panel;
  if (o.wizard === 'skip' || o.wizard === 'auto') intent.wizard = o.wizard;
  if (typeof o.shapeSet === 'string') intent.shapeSet = o.shapeSet;
  if (typeof o.src === 'string') intent.src = o.src;
  const seed = coerceStartSeed(o.seed);
  if (seed) intent.seed = seed;
  return intent;
}

/**
 * The wizard rule (the core flag pass-through):
 *   - seed present        → skip (intent already expressed)
 *   - wizard === 'skip'   → skip (forced — agent/demo/sales links)
 *   - otherwise           → domain decides (return false; caller's own logic runs)
 */
export function shouldSkipWizard(intent: StartIntent | null | undefined): boolean {
  if (!intent) return false;
  if (intent.seed) return true;
  if (intent.wizard === 'skip') return true;
  return false;
}
